import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageShell from "@/components/brand/PageShell";
import GoogleButton from "@/components/brand/GoogleButton";
import { api } from "@/lib/api";

function SignupForm({ role, testIdPrefix }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            await api.register({ email, password, name, role });
            navigate(`/verify-email?email=${encodeURIComponent(email)}`, { replace: true });
        } catch (err) {
            setError(err.message || "Unable to register.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid={`${testIdPrefix}-form`}>
            <div>
                <label htmlFor={`${testIdPrefix}-name`} className="st-label">Full name</label>
                <input
                    id={`${testIdPrefix}-name`}
                    required
                    className="st-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid={`${testIdPrefix}-name-input`}
                />
            </div>
            <div>
                <label htmlFor={`${testIdPrefix}-email`} className="st-label">Email</label>
                <input
                    id={`${testIdPrefix}-email`}
                    type="email"
                    required
                    autoComplete="email"
                    className="st-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={`${testIdPrefix}-email-input`}
                />
            </div>
            <div>
                <label htmlFor={`${testIdPrefix}-password`} className="st-label">Password</label>
                <input
                    id={`${testIdPrefix}-password`}
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="st-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid={`${testIdPrefix}-password-input`}
                />
                <p className="mt-1.5 text-xs" style={{ color: "var(--st-silver-500)" }}>
                    Minimum 8 characters.
                </p>
            </div>

            {error && (
                <div
                    className="text-sm px-3 py-2 rounded"
                    style={{ background: "#FEF1F2", color: "#B42318", border: "1px solid #FECDCA" }}
                    data-testid={`${testIdPrefix}-error`}
                >
                    {error}
                </div>
            )}

            <button
                type="submit"
                disabled={submitting}
                className="btn-royal w-full"
                data-testid={`${testIdPrefix}-submit-btn`}
            >
                {submitting ? "Creating account…" : "Create account"}
            </button>
        </form>
    );
}

export function StudentSignup() {
    return (
        <PageShell>
            <section className="max-w-md mx-auto px-6 py-16" data-testid="signup-page">
                <div className="st-surface p-8">
                    <div
                        className="tracking-caps text-[11px] font-semibold"
                        style={{ color: "var(--st-royal)" }}
                    >
                        Student enrollment
                    </div>
                    <h1
                        className="font-serif text-3xl mt-2"
                        style={{ color: "var(--st-navy)" }}
                        data-testid="signup-heading"
                    >
                        Begin your curriculum
                    </h1>
                    <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                        By enrolling you agree to our{" "}
                        <Link to="/terms" className="link-royal">Terms</Link>,{" "}
                        <Link to="/privacy" className="link-royal">Privacy Policy</Link>, and{" "}
                        <Link to="/risk-disclosure" className="link-royal">Risk Disclosure</Link>.
                    </p>

                    <SignupForm role="student" testIdPrefix="signup" />

                    <div className="my-6 flex items-center gap-3 text-xs" style={{ color: "var(--st-silver-500)" }}>
                        <div className="flex-1 h-px" style={{ background: "var(--st-line)" }} />
                        <span className="tracking-caps">or</span>
                        <div className="flex-1 h-px" style={{ background: "var(--st-line)" }} />
                    </div>

                    <GoogleButton role="student" label="Continue with Google" />

                    <p className="mt-6 text-sm text-center" style={{ color: "var(--st-ink-muted)" }}>
                        Already enrolled?{" "}
                        <Link to="/login" className="link-royal" data-testid="signup-login-link">Sign in</Link>
                    </p>
                </div>
            </section>
        </PageShell>
    );
}

export function AffiliateSignup() {
    return (
        <PageShell>
            <section className="max-w-md mx-auto px-6 py-16" data-testid="affiliate-signup-page">
                <div className="st-surface p-8">
                    <div
                        className="tracking-caps text-[11px] font-semibold"
                        style={{ color: "var(--st-royal)" }}
                    >
                        Affiliate program
                    </div>
                    <h1
                        className="font-serif text-3xl mt-2"
                        style={{ color: "var(--st-navy)" }}
                        data-testid="affiliate-signup-heading"
                    >
                        Partner with SwingTrust
                    </h1>
                    <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                        Refer disciplined students to our academy and earn transparent,
                        long-term commissions.
                    </p>

                    <div className="mt-4 rounded-md p-3 text-xs leading-relaxed"
                         style={{ background: "#FFFAEB", color: "#7A2E0E", border: "1px solid #FEDF89" }}
                         data-testid="affiliate-signup-pending-note">
                        Affiliate accounts require <strong>admin approval</strong> before commissions
                        are earned. You'll be notified once approved. You can start sharing your ref
                        link before approval — clicks are still tracked.
                    </div>

                    <SignupForm role="affiliate" testIdPrefix="affiliate-signup" />

                    <div className="my-6 flex items-center gap-3 text-xs" style={{ color: "var(--st-silver-500)" }}>
                        <div className="flex-1 h-px" style={{ background: "var(--st-line)" }} />
                        <span className="tracking-caps">or</span>
                        <div className="flex-1 h-px" style={{ background: "var(--st-line)" }} />
                    </div>

                    <GoogleButton role="affiliate" label="Continue with Google" />

                    <p className="mt-6 text-sm text-center" style={{ color: "var(--st-ink-muted)" }}>
                        Already a partner?{" "}
                        <Link to="/login" className="link-royal" data-testid="affiliate-login-link">Sign in</Link>
                    </p>
                </div>
            </section>
        </PageShell>
    );
}
